import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Sparkline } from '@/components/ui/Sparkline';
import { Skeleton } from '@/components/ui/Skeleton';

interface KpiCardProps {
  label: string;
  value: string;
  change?: number; // em %
  data?: number[];
  /** Cor do sparkline (hex ou CSS var). Ex: '#CB6A1E' */
  accent?: string;
  loading?: boolean;
  /** Texto de comparação. Default: "vs mês passado". */
  changeLabel?: string;
  className?: string;
}

export function KpiCard({
  label,
  value,
  change,
  data,
  accent = 'var(--color-brand-500, #CB6A1E)',
  loading = false,
  changeLabel = 'vs mês passado',
  className = '',
}: KpiCardProps) {
  if (loading) {
    return (
      <Card className={['p-[18px]', className].join(' ')}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-7 w-28" />
            <Skeleton className="mt-3 h-3 w-32" />
          </div>
          <Skeleton className="h-10 w-20" />
        </div>
      </Card>
    );
  }

  const hasChange = typeof change === 'number' && isFinite(change);
  const isPositive = hasChange && change! >= 0;
  const ChangeIcon = isPositive ? ArrowUp : ArrowDown;
  const changeColor = isPositive
    ? 'text-success dark:text-success-dark'
    : 'text-danger dark:text-danger-dark';
  const sign = isPositive ? '+' : '';

  return (
    <Card className={['p-[18px]', className].join(' ')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-medium uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">
            {label}
          </div>
          <div className="mt-2 text-[26px] font-semibold tracking-[-0.02em] leading-none text-ink-title dark:text-ink-title-dark tabular">
            {value}
          </div>
          {hasChange && (
            <div className="mt-2.5 flex items-center gap-1.5 text-xs">
              <span className={['inline-flex items-center gap-0.5 font-semibold tabular', changeColor].join(' ')}>
                <ChangeIcon className="w-3 h-3" strokeWidth={2.6} />
                {sign}
                {change!.toFixed(change! % 1 === 0 ? 0 : 1)}%
              </span>
              <span className="text-ink-subtitle dark:text-ink-subtitle-dark font-normal">
                {changeLabel}
              </span>
            </div>
          )}
        </div>
        {data && data.length > 1 && (
          <div className="flex-shrink-0 text-ink-subtitle dark:text-ink-subtitle-dark" style={{ color: accent }}>
            <Sparkline data={data} color={accent} filled width={100} height={42} ariaLabel={`Tendência de ${label}`} />
          </div>
        )}
      </div>
    </Card>
  );
}
